import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  checkPaymentStatus,
  getStoredPaymentSession,
  clearPaymentSession,
  updateOrganizationPlan,
  PaymentStatusResponse,
} from '../services/montyPayService.ts';
import { Theme } from '../lib/theme';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';

type PaymentStatusType = 'loading' | 'success' | 'error';

const MontyPaySuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<PaymentStatusType>('loading');
  const [paymentData, setPaymentData] = useState<PaymentStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        // Try to get auth token from localStorage (for new tab) or sessionStorage (same tab)
        let authToken = localStorage.getItem('montypay_auth_token') || sessionStorage.getItem('authToken');
        
        if (!authToken) {
          setError('Authentication required');
          setStatus('error');
          return;
        }

        // Get order_id from URL or localStorage
        const orderIdFromUrl = searchParams.get('order_id');
        const { orderId: storedOrderId } = getStoredPaymentSession();
        const orderId = orderIdFromUrl || storedOrderId;

        if (!orderId) {
          setError('No order ID found. Please contact support if you were charged.');
          setStatus('error');
          return;
        }

        console.log('Fetching payment details for order:', orderId);

        // ✅ Single API call - no retries needed
        // If MontyPay redirected user here, payment is already successful
        const paymentStatus = await checkPaymentStatus(orderId, authToken);
        
        console.log('Payment details received:', paymentStatus);
        setPaymentData(paymentStatus);
        
        // ✅ Trust the redirect - if we're on this page, payment succeeded
        // MontyPay only redirects to success_url on successful payment
        setStatus('success');
        
        // Update organization plan to "custom" after successful payment
        try {
          // Get orgId from sessionStorage (should be available from parent window)
          const orgId = sessionStorage.getItem('orgId') || localStorage.getItem('montypay_org_id');
          
          if (orgId) {
            console.log('Updating organization plan for:', orgId);
            await updateOrganizationPlan(orgId, authToken);
            console.log('Organization plan updated successfully to "custom"');
            toast.success('Your account has been upgraded!');
          } else {
            console.warn('No orgId found - plan not updated');
          }
        } catch (planError) {
          console.error('Failed to update organization plan:', planError);
          // Don't fail the whole success page if plan update fails
          // The payment was successful, just log the error
        }
        
        // 🔔 NOTIFY PARENT WINDOW ABOUT SUCCESS (NO POLLING!)
        notifyParentWindow(orderId);
        
        // Clean up localStorage and sessionStorage
        clearPaymentSession();
        localStorage.removeItem('montypay_auth_token');
        localStorage.removeItem('montypay_user_email');
        localStorage.removeItem('montypay_org_id');

        // Show success message
        toast.success('Payment completed successfully!');


      } catch (err: any) {
        console.error('Error fetching payment details:', err);
        
        // ✅ Still treat as success since MontyPay redirected user here
        // Webhook will update database, or it already did
        const fallbackOrderId = searchParams.get('order_id') || 'unknown';
        
        setPaymentData({ 
          success: true,
          status: 'settled',
          payment_status: 'completed',
          session_id: '',
          order_id: fallbackOrderId,
          message: 'Payment successful! Processing your order...'
        });
        setStatus('success');
        
        // 🔔 NOTIFY PARENT WINDOW EVEN ON ERROR
        notifyParentWindow(fallbackOrderId);
        
        // Clean up localStorage even on error
        clearPaymentSession();
        localStorage.removeItem('montypay_auth_token');
        localStorage.removeItem('montypay_user_email');
        localStorage.removeItem('montypay_org_id');
        
        toast.success('Payment completed successfully!');
      }
    };

    // 🔔 Function to notify parent window about payment success (NO POLLING!)
    const notifyParentWindow = (orderId: string) => {
      try {
        console.log('🔔 Notifying parent window about payment success...');

        // Method 1: BroadcastChannel API (modern browsers, same origin)
        if (typeof BroadcastChannel !== 'undefined') {
          const channel = new BroadcastChannel('montypay_payment');
          channel.postMessage({
            type: 'PAYMENT_SUCCESS',
            orderId: orderId,
            timestamp: Date.now()
          });
          channel.close();
          console.log('✅ Payment success broadcasted via BroadcastChannel');
        }

        // Method 2: LocalStorage event (best cross-browser support)
        // This triggers 'storage' event on other tabs
        const eventData = JSON.stringify({
          orderId: orderId,
          timestamp: Date.now()
        });
        localStorage.setItem('montypay_payment_success', eventData);
        
        // Remove after a short delay to allow event to fire
        setTimeout(() => {
          localStorage.removeItem('montypay_payment_success');
        }, 500);
        
        console.log('✅ Payment success stored in localStorage');

        // Method 3: PostMessage to opener window (if opened via window.open)
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            type: 'PAYMENT_SUCCESS',
            orderId: orderId,
            timestamp: Date.now()
          }, window.location.origin);
          console.log('✅ Payment success sent to opener window via postMessage');
        }

      } catch (error) {
        console.error('Error notifying parent window:', error);
      }
    };

    fetchPaymentDetails();
  }, [searchParams]);

  // Loading state
  if (status === 'loading') {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: `linear-gradient(to bottom right, 
            ${Theme.colors.gradient.secondary1}, 
            ${Theme.colors.gradient.primary1}, 
            ${Theme.colors.background})`,
        }}
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <FaSpinner className="animate-spin text-6xl text-blue-600 mx-auto" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Loading Payment Details
          </h2>
          <p className="text-gray-600 mb-4">
            Please wait while we fetch your payment information...
          </p>
          <p className="text-sm text-gray-500">
            This will only take a moment.
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          background: `linear-gradient(to bottom right, 
            ${Theme.colors.gradient.secondary1}, 
            ${Theme.colors.gradient.primary1}, 
            ${Theme.colors.background})`,
        }}
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="mb-6">
            <div className="bg-green-100 rounded-full w-24 h-24 mx-auto flex items-center justify-center">
              <FaCheckCircle className="text-6xl text-green-600" />
            </div>
          </div>
          
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Payment Successful!
          </h2>
          
          <p className="text-gray-600 mb-6">
            Your payment has been processed successfully.
          </p>

          {paymentData && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-gray-800 mb-3">Payment Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Order ID:</span>
                  <span className="font-mono text-gray-800">{paymentData.order_id}</span>
                </div>
                {paymentData.transaction_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Transaction ID:</span>
                    <span className="font-mono text-gray-800">{paymentData.transaction_id}</span>
                  </div>
                )}
                {paymentData.amount && paymentData.currency && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-semibold text-gray-800">
                      {paymentData.currency} {paymentData.amount}
                    </span>
                  </div>
                )}
                {paymentData.description && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Description:</span>
                    <span className="text-gray-800">{paymentData.description}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <button
            onClick={() => navigate('/history')}
            className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition-colors mb-3"
          >
            Go to Dashboard
          </button>

          <button
            onClick={() => navigate('/new-presentation')}
            className="w-full bg-gray-100 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-200 transition-colors mb-4"
          >
            Create New Presentation
          </button>

          <p className="text-sm text-gray-500">
            A confirmation email has been sent to your email address.
          </p>
        </div>
      </div>
    );
  }

  // Error state (should rarely happen since MontyPay only redirects on success)
  // This is mainly for authentication errors or missing order_id
  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(to bottom right, 
          ${Theme.colors.gradient.secondary1}, 
          ${Theme.colors.gradient.primary1}, 
          ${Theme.colors.background})`,
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
        <div className="mb-6">
          <div className="bg-red-100 rounded-full w-24 h-24 mx-auto flex items-center justify-center">
            <FaTimesCircle className="text-6xl text-red-600" />
          </div>
        </div>
        
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          Unable to Load Payment Details
        </h2>
        
        <p className="text-gray-600 mb-6">
          {error || 'We could not load your payment information.'}
        </p>

        <p className="text-sm text-gray-500 mb-6">
          Don't worry - if you were charged, your payment was successful. 
          Please check your email for confirmation or contact support.
        </p>

        {paymentData && paymentData.order_id && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
            <div className="text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Order ID:</span>
                <span className="font-mono text-gray-800">{paymentData.order_id}</span>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Please save this order ID for reference when contacting support.
            </p>
          </div>
        )}

        <button
          onClick={() => navigate('/new-presentation')}
          className="w-full bg-blue-600 text-white font-semibold py-3 rounded-lg hover:bg-blue-700 transition-colors mb-3"
        >
          Try Again
        </button>

        <button
          onClick={() => navigate('/contact-us')}
          className="w-full bg-gray-100 text-gray-700 font-semibold py-3 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Contact Support
        </button>
      </div>
    </div>
  );
};

export default MontyPaySuccessPage;
